import { useAuth } from "@/context/AuthContext";
import { CalendarClock, Pencil, Trash2, AlertTriangle, Scale } from "lucide-react";

const PRIORITY = {
  urgent: "bg-red-100 text-red-700", high: "bg-orange-100 text-orange-700",
  normal: "bg-gray-100 text-gray-600", low: "bg-slate-100 text-slate-500",
};
const STATUS = {
  open: "bg-blue-50 text-blue-700", pending: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
};
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

function DateChip({ label, value, danger }) {
  if (!value) return null;
  return (
    <span className={`text-[11px] font-semibold ${danger ? "text-red-600" : "text-gray-500"}`}>
      {label}: {value.slice(0, 10)}
    </span>
  );
}

export default function DocketList({ entries, showClient, onEdit, onDelete, onStatus, statuses }) {
  const { user } = useAuth();
  if (!entries.length) {
    return <p className="text-xs text-gray-400 text-center py-8 border border-dashed border-gray-200 rounded-sm" data-testid="docket-empty">No docket entries.</p>;
  }
  return (
    <div className="space-y-2" data-testid="docket-list">
      {entries.map((e) => (
        <div key={e.id} data-testid={`docket-row-${e.id}`}
          className="bg-white border border-gray-200 rounded-sm p-4 flex items-start justify-between gap-4 hover:border-gray-300 transition-colors">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#002FA7]/10 text-[#002FA7]">{e.entry_type}</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${PRIORITY[e.priority] || PRIORITY.normal}`}>{cap(e.priority)}</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS[e.status] || ""}`} data-testid={`docket-status-${e.id}`}>{cap(e.status)}</span>
              {e.outcome && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{e.outcome}</span>}
              {e.billable && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">Billable</span>}
            </div>
            <p className="font-semibold text-gray-900 mt-1.5">{e.title}</p>
            {showClient && e.client_name && <p className="text-xs text-gray-500">{e.client_name}</p>}
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <DateChip label="Event" value={e.event_date} />
              <DateChip label="Due" value={e.due_date} />
              <DateChip label="Court" value={e.court_date} />
              {e.sol_date && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600">
                  <Scale className="w-3 h-3" /> SOL: {e.sol_date.slice(0, 10)}
                </span>
              )}
            </div>
            {(e.assigned_attorney_name || e.assigned_staff_name || e.responsible_team) && (
              <p className="text-xs text-gray-400 mt-1">
                {[e.assigned_attorney_name && `Atty: ${e.assigned_attorney_name}`,
                  e.assigned_staff_name && `Staff: ${e.assigned_staff_name}`,
                  e.responsible_team].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <select value={e.status} onChange={(ev) => onStatus(e, ev.target.value)} data-testid={`docket-status-select-${e.id}`}
              className="text-xs border border-gray-300 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {(statuses || ["open", "pending", "completed"]).map((s) => <option key={s} value={s}>{cap(s)}</option>)}
            </select>
            <button onClick={() => onEdit(e)} data-testid={`docket-edit-${e.id}`} className="text-gray-400 hover:text-[#002FA7] p-1.5"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => onDelete(e)} data-testid={`docket-delete-${e.id}`} className="text-gray-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}
